import { Inject, Injectable } from '@angular/core';
import { ConfigInstanceToken, ConfigInterface } from '../../interfaces/config.interface';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { OfflineService } from '@evorait/pace-offline';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, combineLatest, from, Observable, of, throwError } from 'rxjs';
import { catchError, filter, finalize, map, mergeMap, scan, startWith, switchMap, tap } from 'rxjs/operators';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { ConfigRequestsInstanceToken, ConfigRequestsInterface } from '../../interfaces/config-requests.interface';
import { InitializableInterface } from '../../interfaces/initializable.interface';
import { InitializationInterface } from '../../interfaces/initialization.interface';
import { SideMenuInstanceToken, SideMenuInterface } from '../../interfaces/side-menu.interface';
import { SynchronizationInstanceToken, SynchronizationInterface } from '../../interfaces/synchronization.interface';
import { AppNotificationComponent } from '../app-notification/app-notification.component';
import { AppNotificationModel, AppNotificationType } from '../app-notification/app-notification.model';
import { AppNotificationService } from '../app-notification/app-notification.service';
import { ConnectivityService } from '../connectivity/connectivity.service';
import { EventService } from '../event/event.service';
import { LocalStorageAppStateService } from '../local-storage/local-storage-app-state.service';
import { UriUtilitiesService } from '../uri-utilities/uri-utilities.service';
import { UserGidService } from '../user-gid/user-gid.service';
import { UserMasterInstanceToken, UserMasterInterface } from '../../interfaces/user-master.interface';
import { InitError } from './init-error';
import { INIT_MESSAGE_KEY, INIT_MESSAGE_STATE, InitMessage } from './init-message';
import { LocalStorageCommonService } from '../local-storage/local-storage-common.service';
import { FileStorageInjectionToken, FileStorageServiceInterface } from '../../interfaces/file-storage.interface';
import { EncryptedDataHost, EncryptionMigrator } from '@evorait/pace-encryption';
import { EncryptionInitializer } from '../encryption/encryption-initialiser.service';

@Injectable()
export abstract class BaseInitializationService implements InitializableInterface, InitializationInterface {
    // initialize the appStartTimer in the app. Decoupling the startTimer from the attachStartupPreparation
    public appStartTimer = moment();
    public upgradePerformed = false;
    public authenticationIncomplete = false;
    public appVersion: string;

    public init$: Observable<unknown>;
    public modalInstance: BsModalRef;
    public initMessagesSubject: BehaviorSubject<InitMessage>;
    public initMessages$: Observable<InitMessage[]>;
    public encDataHost: EncryptedDataHost[];

    public isInitialized = false;

    protected constructor(
        @Inject(LogInstanceToken) protected readonly log: LogInterface,
        @Inject(ClientManagerInstanceToken) protected readonly clientManager: ClientManagerInterface,
        @Inject(SideMenuInstanceToken) protected readonly sideMenuService: SideMenuInterface,
        @Inject(ConfigRequestsInstanceToken) protected readonly configInterfaceService: ConfigRequestsInterface,
        protected readonly modalService: BsModalService,
        protected readonly localStorageAppStateService: LocalStorageAppStateService,
        protected readonly connectivityService: ConnectivityService,
        protected readonly uriUtilities: UriUtilitiesService,
        @Inject(UserMasterInstanceToken) protected readonly userMasterService: UserMasterInterface,
        protected readonly appNotificationService: AppNotificationService,
        protected readonly translate: TranslateService,
        protected readonly eventService: EventService,
        @Inject(SynchronizationInstanceToken)
        protected readonly synchronizationService: SynchronizationInterface,
        @Inject(ConfigInstanceToken) protected readonly configService: ConfigInterface,
        protected readonly offlineService: OfflineService,
        protected readonly userGidService: UserGidService,
        protected readonly localStorageCommonService: LocalStorageCommonService,
        @Inject(FileStorageInjectionToken) protected readonly fileStorageServiceInterface: FileStorageServiceInterface,
        protected readonly encryptionMigrator: EncryptionMigrator,
        protected readonly encryptionInitializer: EncryptionInitializer,
    ) {
        this.encDataHost = [
            { name: 'localStorage', host: this.localStorageCommonService },
            { name: 'fileStorage', host: this.fileStorageServiceInterface },
            { name: 'logs', host: this.log },
            { name: 'offlineData', host: this.offlineService },
        ];

        this.initMessagesSubject = new BehaviorSubject<InitMessage>(
            new InitMessage(
                INIT_MESSAGE_KEY.APP_START,
                INIT_MESSAGE_STATE.PENDING,
                this.translate.stream('APP_START_INFO_STARTING'),
                of('0s'),
            ),
        );

        this.initMessages$ = this.initMessagesSubject.asObservable().pipe(
            scan((acc: InitMessage[], msg: InitMessage) => {
                if (msg.key === INIT_MESSAGE_KEY.CLEAR_MESSAGES) {
                    acc = [];
                } else {
                    const index = acc.findIndex((initMessage: InitMessage) => initMessage.key === msg.key);

                    if (index > -1) {
                        acc[index] = msg;
                    } else {
                        acc.push(msg);
                    }
                }

                return acc;
            }, []),
        );
    }

    public checkInit(): Observable<boolean> {
        return this.isInitialized
            ? of(true) // if already initialized, init was definitely successful
            : this.init().pipe(
                  map(() => true), // if initialization was successful, return true
                  catchError(() => of(false)), // otherwise return false
              );
    }

    protected abstract setupInitProcessPipe(): void;

    // Function to load any effects that should happen after initialization is completed.
    protected abstract executeRemainingSideEffects(): void;

    public init(): Observable<void> {
        this.clearInitMessages();
        this.setupInitProcessPipe();

        this.offlineService.setAppInitializationCompleted(false);

        this.init$ = this.attachServiceInit(this.init$);
        // config service should have all data now so that next init step can work with config parameters
        this.init$ = this.attachConfigInterfaceInit(this.init$);
        this.init$ = this.attachSideMenuInit(this.init$);

        return this.init$.pipe(
            tap(() => this.executeRemainingSideEffects()),
            catchError((error) => {
                if (this.errorCanBeHandled(error)) {
                    return of(undefined);
                }

                return throwError(() => error);
            }),
            map(() => {
                this.isInitialized = true;
                // needed to notify the app-update.service when the app is initialized.
                this.eventService.triggerAppInitializationComplete();
                this.offlineService.setAppInitializationCompleted(true);
            }),
        );
    }

    // TODO add logic for the service initialization
    public abstract attachServiceInit(obs$: Observable<unknown>): Observable<void>;

    protected clearInitMessages(): void {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.CLEAR_MESSAGES,
                INIT_MESSAGE_STATE.PENDING,
                of('CLEAR MESSAGES'),
                of('0s'),
            ),
        );
    }

    /**
     * Wrapper function to trigger reinitialization of services from the outside
     *
     * @returns observable that completes when the initialization is done
     * @throws Error if the service is not initialized
     */
    public initServices(): Observable<void> {
        this.clearInitMessages();

        if (!this.isInitialized) {
            throw new Error(
                `BaseInitializationService: initServices: Trying to call BaseInitializationService before initialization!`,
            );
        }

        return this.attachServiceInit(of(undefined));
    }

    /**
     * Start the timer for the init process
     *
     * @param obs$ the observable to attach the error handler to
     * @returns the observable with the error handler attached
     */
    protected attachStartupPreparation<T>(obs$: Observable<T>): Observable<T> {
        return obs$.pipe(
            tap(() => {
                // reset init variables
                this.authenticationIncomplete = false;
                this.upgradePerformed = false;
            }),
        );
    }

    /**
     *
     * This method is only relevant for the transition period from GW encryption
     * keys to Azure encryption keys. Once, all users that have on encryption
     * key on GW also have an encryption key on Azure, it can safely be removed.
     * ~ summer 2026 should be safe
     *
     * @param obs$ the observable of the init pipe
     * @protected
     * @returns the observable for the init pipe
     */
    protected upgradeLegacyEncryption(obs$: Observable<unknown>): Observable<unknown> {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.LEGACY_ENCRYPTION,
                INIT_MESSAGE_STATE.ADDED,
                this.translate.stream('APP_START_INFO_LEGACY_ENCRYPTION'),
                of(`0s`),
            ),
        );
        let startTimer: moment.Moment;

        return obs$.pipe(
            tap({
                next: () => (startTimer = moment()),
                error: () => (startTimer = moment()),
            }),
            tap(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.LEGACY_ENCRYPTION,
                        INIT_MESSAGE_STATE.PENDING,
                        this.translate.stream('APP_START_INFO_LEGACY_ENCRYPTION_INFO'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
            }),
            switchMap(() => {
                if (this.encryptionMigrator.isMigrated()) {
                    this.log.info(
                        this.constructor.name,
                        'Found encryption marker. Not attempting to migrate legacy encryption migration...',
                    );
                    return of([false]);
                }

                // the encryption marker is not yet present -> need to check if any of the storage backends still contains data with the legacy encryption key
                return combineLatest(this.encDataHost.map((e) => e.host.hasLegacyEncryption()));
            }),
            switchMap((legacyEncryption) => {
                const hasLegacyEncryption = legacyEncryption.some((hasLegacyEncryptedData) => hasLegacyEncryptedData);
                if (!hasLegacyEncryption) {
                    // there is no legacy encryption, so we can just continue
                    if (legacyEncryption.length > 1) {
                        // there was no migration marker, yet -> lets set one
                        this.log.info(
                            this.constructor.name,
                            'setting encryption marker as no encrypting storage backend contains legacy encrypted data',
                        );
                        this.encryptionMigrator.setMigrated();
                    }
                    return of(undefined);
                }

                this.log.info(
                    this.constructor.name,
                    'need to re-encrypt the data as it was encrypted with a legacy key',
                );
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.LEGACY_ENCRYPTION,
                        INIT_MESSAGE_STATE.PENDING,
                        this.translate.stream('APP_START_INFO_LEGACY_ENCRYPTION_PROGRESS'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );

                // no encryption marker, but legacy encrypted data -> we need to re-encrypt the data
                return this.encryptionInitializer.retrieveLegacyKey().pipe(
                    switchMap((key) => {
                        this.encryptionMigrator.setLegacyKey(key);
                        this.encryptionMigrator.noteMigrationStarted();

                        return combineLatest(this.encDataHost.map((e) => e.host.upgradeEncryption()));
                    }),
                    tap((migrationResult: boolean[]) => {
                        this.log.info(
                            this.constructor.name,
                            'Legacy encryption migration results',
                            this.encDataHost
                                .map((e) => e.name)
                                .map((value, index) => `${value} => ${migrationResult[index]}`)
                                .join(', '),
                        );
                        if (migrationResult.every((result) => result)) {
                            this.encryptionMigrator.setMigrated();
                        }
                    }),
                );
            }),
            tap(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.LEGACY_ENCRYPTION,
                        INIT_MESSAGE_STATE.SUCCESS,
                        this.translate.stream('APP_START_INFO_LEGACY_ENCRYPTION_SUCCESS'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
            }),
            catchError((e) => {
                // und nu?
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.LEGACY_ENCRYPTION,
                        INIT_MESSAGE_STATE.ERROR,
                        this.translate.stream('APP_START_INFO_LEGACY_ENCRYPTION_ERROR'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                throw e;
            }),
        );
    }

    /**
     * Load the version information
     *
     * @param obs$ the observable to attach the error handler to
     * @returns the observable with the error handler attached
     */
    protected attachVersionInformation<T>(obs$: Observable<T>): Observable<T> {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.APP_VERSION,
                INIT_MESSAGE_STATE.ADDED,
                this.translate.stream('APP_START_INFO_LOADING_VERSION_NUMBER'),
                of(`0s`),
            ),
        );
        let startTimer: moment.Moment;
        return obs$.pipe(
            tap(() => (startTimer = moment())),
            tap(() => {
                const appId = this.clientManager.getCurrentAppId();
                this.appVersion = this.clientManager.getAppVersion();

                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.APP_VERSION,
                        this.appVersion ? INIT_MESSAGE_STATE.SUCCESS : INIT_MESSAGE_STATE.WARNING,
                        this.translate.stream('APP_START_INFO_LOADING_VERSION_NUMBER', {
                            appId: `${appId}`,
                            versionInfo: `${this.appVersion}`,
                        }),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                this.log.debug(
                    `BaseInitializationService: attachVersionInformation: app version is: ${this.appVersion}`,
                );
                this.log.debug(
                    `BaseInitializationService: attachVersionInformation: time taken: ${
                        moment().valueOf() - startTimer.valueOf()
                    } ms`,
                );
            }),
        );
    }

    /**
     * Checks if internet connection is available. This is relevant for the successive steps.
     *
     * @param obs$ the observable to attach the error handler to
     * @returns the observable with the error handler attached
     */
    protected attachInternetConnectionCheck(obs$: Observable<unknown>): Observable<void> {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.INTERNET_CONNECTION,
                INIT_MESSAGE_STATE.ADDED,
                this.translate.stream('APP_START_INFO_INTERNET', { connectionType: '' }),
                of(`0s`),
            ),
        );
        let startTimer: moment.Moment;
        let previousError: InitError;
        return obs$.pipe(
            catchError((error) => {
                if (this.errorCanBeHandled(error)) {
                    previousError = error;
                    return of(undefined);
                }
                return throwError(() => error);
            }),
            tap(() => (startTimer = moment())),
            tap(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.INTERNET_CONNECTION,
                        INIT_MESSAGE_STATE.PENDING,
                        this.translate.stream('APP_START_INFO_INTERNET', { connectionType: '' }),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
            }),
            mergeMap(() => this.connectivityService.isOnlineButWithErrors$),
            mergeMap((value) => {
                const connectionType = value ?? '';

                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.INTERNET_CONNECTION,
                        INIT_MESSAGE_STATE.SUCCESS,
                        this.translate.stream('APP_START_INFO_INTERNET', {
                            connectionType: ` (${connectionType})`,
                        }),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                if (previousError) {
                    return throwError(() => previousError);
                }
                return of(undefined);
            }),
            catchError((error) => {
                if (error instanceof InitError) {
                    return throwError(() => error);
                }
                this.log.error(
                    `BaseInitializationService: attachInternetConnectionCheck: ERROR: ${JSON.stringify(
                        error,
                        Object.getOwnPropertyNames(error),
                    )}`,
                );

                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.INTERNET_CONNECTION,
                        INIT_MESSAGE_STATE.WARNING,
                        this.translate.stream('APP_START_INFO_INTERNET', {
                            connectionType: '',
                        }),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                const internetError = new InitError(new Error('No internet connection available'), false);
                internetError.isOfflineError = true;
                return from(this.localStorageAppStateService.getInitialStartFlagAsync()).pipe(
                    mergeMap((initialised) => {
                        if (initialised) {
                            internetError.hardFail = true;
                            this.initMessagesSubject.next(
                                new InitMessage(
                                    INIT_MESSAGE_KEY.INTERNET_CONNECTION,
                                    INIT_MESSAGE_STATE.ERROR,
                                    this.translate.stream('APP_START_INFO_INTERNET', {
                                        connectionType: '',
                                    }),
                                    of(`${moment().diff(startTimer, 'seconds')}s`),
                                ),
                            );
                        }
                        return throwError(() => internetError);
                    }),
                );
            }),
        );
    }

    /**
     * Check and set user master data when working with multiple sales org/plant per user
     *
     * @param obs$ the observable to attach the error handler to
     * @returns the observable with the error handler attached
     */
    protected attachUserMasterServiceInit(obs$: Observable<unknown>): Observable<unknown> {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.USER_MASTER_DATA,
                INIT_MESSAGE_STATE.ADDED,
                this.translate.stream('APP_START_INFO_USER_MASTER', { additionalInfo: '' }),
                of(`0s`),
            ),
        );

        let previousError: InitError;
        let startTimer: moment.Moment;

        return obs$.pipe(
            catchError((error) => {
                if (this.errorCanBeHandled(error)) {
                    previousError = <InitError>error;
                    return of(undefined);
                }

                return throwError(() => error);
            }),
            tap(
                () => (startTimer = moment()),
                () => (startTimer = moment()),
            ),
            tap(() =>
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.USER_MASTER_DATA,
                        INIT_MESSAGE_STATE.PENDING,
                        this.translate.stream('APP_START_INFO_USER_MASTER', { additionalInfo: '' }),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                ),
            ),
            mergeMap(() => this.userMasterService.init()),
            mergeMap(() => {
                const userModel = this.userMasterService.getSelectedUserModel();
                this.userGidService.setUserGid(userModel.getFrontId());
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.USER_MASTER_DATA,
                        INIT_MESSAGE_STATE.SUCCESS,
                        this.translate.stream('APP_START_INFO_USER_MASTER', {
                            additionalInfo: ` (${userModel.getSalesOrg()}, ${userModel.getPlant()})`,
                        }),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );

                if (typeof previousError !== 'undefined') {
                    return throwError(() => previousError);
                }
                return of(undefined);
            }),
            catchError((error) => {
                if (error instanceof InitError) {
                    return throwError(() => error);
                }

                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.USER_MASTER_DATA,
                        INIT_MESSAGE_STATE.ERROR,
                        this.translate.stream('APP_START_ERROR_USER_MASTER_FAILED'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );

                // if we cannot submit user master data, whatsnew & push will not work -> Hard fail
                return throwError(() => new InitError(<Error>error, true));
            }),
            finalize(() =>
                this.log.debug(
                    `BaseInitializationService: attachUserMasterServiceInit: time taken: ${
                        moment().valueOf() - startTimer.valueOf()
                    } ms`,
                ),
            ),
        );
    }

    /**
     * Start timer-based session checking
     *
     * @param obs$ the observable to attach the error handler to
     * @returns the observable with the error handler attached
     */
    protected attachSessionCheck(obs$: Observable<unknown>): Observable<void> {
        let previousError: InitError;
        return obs$.pipe(
            catchError((error) => {
                if (this.errorCanBeHandled(error)) {
                    previousError = error;
                    return of(undefined);
                }
                return throwError(() => error);
            }),
            mergeMap(() => {
                if (typeof previousError !== 'undefined') {
                    return throwError(() => previousError);
                }
                return of(undefined);
            }),
            catchError((error) => {
                if (error instanceof InitError) {
                    return throwError(() => error);
                }
                return throwError(() => new InitError(error, true));
            }),
        );
    }

    /**
     * Load the version information
     *
     * @param obs$ the observable to attach the error handler to
     * @returns the observable with the error handler attached
     */
    protected attachVersionAndSessionSyncWithGateway(obs$: Observable<unknown>): Observable<unknown> {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.SYNC_APP_VERSION,
                INIT_MESSAGE_STATE.ADDED,
                this.translate.stream('APP_START_INFO_SYNC_APP_VERSION'),
                of(`0s`),
            ),
        );
        let startTimer: moment.Moment;
        let previousError: InitError;
        return obs$.pipe(
            catchError((error) => {
                if (this.errorCanBeHandled(error)) {
                    previousError = error;
                    return of(undefined);
                }
                return throwError(() => error);
            }),
            tap(() => (startTimer = moment())),
            tap(() => {
                this.appVersion = this.clientManager.getAppVersion();

                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.SYNC_APP_VERSION,
                        INIT_MESSAGE_STATE.PENDING,
                        this.translate.stream('APP_START_INFO_SYNC_APP_VERSION'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
            }),
            tap(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.SYNC_APP_VERSION,
                        INIT_MESSAGE_STATE.SUCCESS,
                        this.translate.stream('APP_START_INFO_SYNC_APP_VERSION'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                if (previousError) {
                    return throwError(() => previousError);
                }
                return of(undefined);
            }),
            catchError((error) => {
                if (error instanceof InitError) {
                    return throwError(() => error);
                }

                this.log.error(
                    `BaseInitializationService: attachVersionSyncWithGateway: Could not sync app version with GW`,
                );

                if (previousError?.isOfflineError) {
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SYNC_APP_VERSION,
                            INIT_MESSAGE_STATE.WARNING,
                            this.translate.stream('APP_START_INFO_SYNC_APP_VERSION'),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );

                    return throwError(() => previousError);
                }

                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.SYNC_APP_VERSION,
                        INIT_MESSAGE_STATE.ERROR,
                        this.translate.stream('APP_START_INFO_SYNC_APP_VERSION'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );

                return throwError(() => new InitError(error, false));
            }),
        );
    }

    /**
     * Set up the defining requests according to the selected user master data
     *
     * @param obs$ the observable to attach the error handler to
     * @returns the observable with the error handler attached
     */
    protected attachConfigInterfaceInit(obs$: Observable<unknown>): Observable<void> {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.CONFIG_INTERFACE,
                INIT_MESSAGE_STATE.ADDED,
                this.offlineService.request$.pipe(
                    startWith(<string>this.translate.instant('APP_START_INFO_CONFIGURING_INTERFACES')),
                ),
                of(`0s`),
            ),
        );
        let previousError: InitError;
        let startTimer: moment.Moment;

        return obs$.pipe(
            catchError((error) => {
                if (this.errorCanBeHandled(error)) {
                    previousError = <InitError>error;
                    return of(undefined);
                }

                return throwError(() => error);
            }),
            tap(
                () => (startTimer = moment()),
                () => (startTimer = moment()),
            ),
            tap(() =>
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.CONFIG_INTERFACE,
                        INIT_MESSAGE_STATE.PENDING,
                        this.offlineService.request$.pipe(
                            startWith(<string>this.translate.instant('APP_START_INFO_CONFIGURING_INTERFACES')),
                        ),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                ),
            ),
            mergeMap(() => {
                return this.configInterfaceService.init().pipe(
                    mergeMap((definingRequests) => {
                        return this.offlineService
                            .addMultipleRefreshRequest(this.uriUtilities.getUriForContent(), definingRequests)
                            .pipe(filter((x) => x >= definingRequests.length));
                    }),
                    // make sure the pending data is sent first before refresh
                    mergeMap(() =>
                        this.synchronizationService.sendOfflineData().pipe(
                            catchError((error) => {
                                // this sync error should not prevent the app from starting
                                this.log.warn(
                                    `BaseInitializationService syncing offline requests before refreshing get requests. Syncing failed for at least one POST request. Continuing anyway. '${error}'`,
                                );
                                return of(undefined);
                            }),
                        ),
                    ),
                    mergeMap(() => this.synchronizationService.receiveEverythingCacheFirst(false)),
                );
            }),
            mergeMap(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.CONFIG_INTERFACE,
                        INIT_MESSAGE_STATE.SUCCESS,
                        this.translate.stream('APP_START_INFO_CONFIGURING_INTERFACES'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );

                if (typeof previousError !== 'undefined') {
                    return throwError(() => previousError);
                }

                return of(undefined);
            }),
            catchError((error) => {
                if (error instanceof InitError) {
                    return throwError(() => error);
                }

                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.CONFIG_INTERFACE,
                        INIT_MESSAGE_STATE.ERROR,
                        this.translate.stream('APP_START_ERROR_CONFIG_INTERFACE_FAILED'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );

                // if w e cannot configure the defining requests, opening the offline store will fail -> hard fail
                return throwError(() => new InitError(<Error>error, true));
            }),
            finalize(() =>
                this.log.debug(
                    `BaseInitializationService: attachConfigInterfaceInit: time taken: ${
                        moment().valueOf() - startTimer.valueOf()
                    } ms`,
                ),
            ),
        );
    }

    /**
     * Stop the app start timer and log the duration
     *
     * @param obs$ the observable to attach the error handler to
     * @returns the observable with the error handler attached
     */
    protected attachStartupErrorHandler<T>(obs$: Observable<T>): Observable<T | undefined> {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.APP_START,
                INIT_MESSAGE_STATE.ADDED,
                this.translate.stream('APP_START_ERROR_GENERAL_PARTIALLY_FAILED'),
                of(`0s`),
            ),
        );
        return obs$.pipe(
            catchError((error) => {
                const startTimer = moment();
                if (error instanceof InitError) {
                    // if the error is not hardFail then let the app initialization continue
                    if (!error.hardFail) {
                        this.initMessagesSubject.next(
                            new InitMessage(
                                INIT_MESSAGE_KEY.APP_START,
                                INIT_MESSAGE_STATE.WARNING,
                                this.translate.stream('APP_START_ERROR_GENERAL_PARTIALLY_FAILED'),
                                of(`${moment().diff(startTimer, 'seconds')}s`),
                            ),
                        );

                        this.authenticationIncomplete = true;
                        return of(undefined);
                    } else if (error.isOfflineError) {
                        // if internet connection is not available show pop up
                        this.openNoInternetConnectionPopup();
                    } else if (error.isDateTimeError) {
                        this.log.error(
                            `BaseInitializationService: attachStartupErrorHandler: ERROR: ${JSON.stringify(
                                error,
                                Object.getOwnPropertyNames(error),
                            )}`,
                        );
                        this.initMessagesSubject.next(
                            new InitMessage(
                                INIT_MESSAGE_KEY.DATE_TIME_CHECK,
                                INIT_MESSAGE_STATE.ERROR,
                                this.translate.stream('APP_START_CHECK_DATE_AND_TIME_FAILED'),
                                of(`${moment().diff(startTimer, 'seconds')}s`),
                            ),
                        );
                        return throwError(() => error);
                    }
                }

                // unkn own error or hard failure occurred; interrupt startup now.
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.APP_START,
                        INIT_MESSAGE_STATE.ERROR,
                        this.translate.stream('APP_START_ERROR_START_INTERRUPTED'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );

                this.log.debug(
                    `BaseInitializationService: attachStartupErrorHandler: time taken: ${
                        moment().valueOf() - startTimer.valueOf()
                    } ms`,
                );

                return throwError(() => error);
            }),
        );
    }

    protected attachSideMenuInit(obs$: Observable<unknown>): Observable<void> {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.SIDE_MENU,
                INIT_MESSAGE_STATE.ADDED,
                this.translate.stream('APP_START_SETUP_SIDE_MENU'),
                of(`0s`),
            ),
        );
        let previousError: InitError;
        let startTimer: moment.Moment;
        return obs$.pipe(
            catchError((error) => {
                if (this.errorCanBeHandled(error)) {
                    previousError = <InitError>error;
                    return of(undefined);
                }

                return throwError(() => error);
            }),
            tap(
                () => (startTimer = moment()),
                () => (startTimer = moment()),
            ),
            tap(() =>
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.SIDE_MENU,
                        INIT_MESSAGE_STATE.PENDING,
                        this.translate.stream('APP_START_SETUP_SIDE_MENU'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                ),
            ),
            mergeMap(() => this.sideMenuService.init()),
            map(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.SIDE_MENU,
                        INIT_MESSAGE_STATE.SUCCESS,
                        this.translate.stream('APP_START_SETUP_SIDE_MENU'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );

                if (typeof previousError !== 'undefined') {
                    throw previousError;
                }
            }),
            catchError((error) => {
                if (error instanceof InitError) {
                    return throwError(() => error);
                }

                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.SIDE_MENU,
                        INIT_MESSAGE_STATE.ERROR,
                        this.translate.stream('APP_START_SETUP_SIDE_MENU'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );

                return of(undefined);
            }),
        );
    }

    /**
     * Stop the app start timer and log the duration
     *
     * @param obs$ the observable to attach the error handler to
     * @returns the observable with the error handler attached
     */
    protected attachInitDurationLogger(obs$: Observable<unknown>): Observable<void> {
        let previousError: unknown;
        return obs$.pipe(
            catchError((error) => {
                previousError = error;
                return of(undefined);
            }),
            map(() => {
                this.log.debug(
                    `BaseInitializationService: attachInitDurationLogger: Total App Start Duration: ${
                        moment().valueOf() - this.appStartTimer.valueOf()
                    } ms`,
                );
            }),
            mergeMap((result) => {
                if (typeof previousError !== 'undefined') {
                    return throwError(() => previousError);
                }
                return of(result);
            }),
        );
    }

    /**
     * Save the app initial start state
     *
     * @param obs$ the observable to attach the error handler to
     * @returns the observable with the error handler attached
     */
    protected attachAppInitStateChange(obs$: Observable<unknown>): Observable<void> {
        let previousError: InitError;
        return obs$.pipe(
            catchError((error) => {
                if (this.errorCanBeHandled(error)) {
                    previousError = <InitError>error;
                    return of(undefined);
                }

                return throwError(() => error);
            }),
            mergeMap(() => {
                // only set the flag that we are successfully initialized, when the authentication
                // was  successful this time
                if (!this.authenticationIncomplete) {
                    return this.localStorageAppStateService.setInitialStartFlag(false);
                }
                return of();
            }),
            mergeMap((result) => {
                if (typeof previousError !== 'undefined') {
                    return throwError(() => previousError);
                }
                return of(result);
            }),
        );
    }

    /**
     * Decide if the error can be handled or not: This is the case when the error was either caused by being offline or
     * by a failing authentication (eg. entitlement down or PKI expired), but the hard fail flag is not set (ie. this is
     * not the first start of the app and we already have data in the offline store that can be used).
     *
     * @param error the error to check
     * @returns true if the error can be handled, false otherwise
     */
    protected errorCanBeHandled(error: InitError | unknown): boolean {
        return (
            error instanceof InitError &&
            !error.isDateTimeError &&
            (error.isOfflineError || error.isAuthenticationError) &&
            !error.hardFail
        );
    }

    /**
     * Open a modal with the message that no internet connection is available,
     * only if it is the first start of the application
     */
    private openNoInternetConnectionPopup(): void {
        const type = AppNotificationType.ERROR;
        const titleText = <string>this.translate.instant('APP_START_ERROR_START_IN_OFFLINE_MODE');
        const bodyText = <string>this.translate.instant('APP_START_ERROR_NO_INTERNET_HARD');

        this.modalService.show(AppNotificationComponent, {
            backdrop: 'static',
            class: 'modal-md',
            initialState: {
                model: new AppNotificationModel(type, titleText, bodyText),
            },
            ariaLabelledBy: 'app-notification-modal-title',
        });
    }

    protected attachRehydration(): Observable<undefined> {
        return this.offlineService.isRehydrationCompleted$.pipe(
            filter((x) => x),
            map(() => undefined),
        );
    }
}
